import { ref, onMounted, onBeforeUnmount, watch, nextTick, type Ref } from "vue";

type Id = string | number;

interface ScrollSpyOptions {
  resolveContainer: () => HTMLElement | null;
  elementId: (id: Id) => string;
  offset?: number;
}

/**
 * Tracks which of `ids` is the current one while scrolling: the last element that has
 * passed `offset` from the top of the container.
 *
 * Elements are resolved by DOM id on every pass rather than held as refs, so targets
 * that mount and unmount freely (lazy-loaded, collapsed) never leave a stale
 * reference behind. Scroll handling is throttled to one computation per frame.
 */
export function useScrollSpy(ids: Ref<Id[]>, options: ScrollSpyOptions) {
  const { resolveContainer, elementId, offset = 120 } = options;

  const activeId = ref<Id | null>(null);
  let container: HTMLElement | null = null;
  let ticking = false;

  function computeActive() {
    ticking = false;
    if (!container || !ids.value.length) return;
    const containerTop = container.getBoundingClientRect().top;
    let current = ids.value[0];
    for (const id of ids.value) {
      const el = document.getElementById(elementId(id));
      if (!el) continue;
      if (el.getBoundingClientRect().top - containerTop <= offset) current = id;
      else break;
    }
    activeId.value = current;
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(computeActive);
  }

  onMounted(async () => {
    await nextTick();
    container = resolveContainer();
    container?.addEventListener("scroll", onScroll, { passive: true });
    computeActive();
  });

  watch(ids, () => nextTick(computeActive));

  onBeforeUnmount(() => {
    container?.removeEventListener("scroll", onScroll);
  });

  return { activeId };
}
